import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BookOpen, Shuffle, Zap, Rocket, Building2, ListOrdered, ScanSearch, Puzzle, Brain, Headphones, Layers, Grid } from "lucide-react";
import { Deck } from "@/types/linguadeck";
import type { StudyModes } from "@/types/linguadeck";
import { fetchSharedDeck, createDeck, fetchDecksLight, DeckAlreadyImportedError } from "@/lib/db";
import { inspectQueryError, validateWriteReadConsistency, shouldValidateReadAfterWrite } from "@/diagnostics";
import { useSafeAsyncData } from "@/hooks/useSafeAsyncData";
import { triggerBackgroundTtsForCards } from "@/lib/triggerBackgroundTts";
import ExplainModal from "@/components/ExplainModal";
import FlashcardView from "@/components/FlashcardView";
import LearnMode from "@/components/LearnMode";
import TestStudyView from "@/components/TestStudyView";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import ThemeToggle from "@/components/ThemeToggle";
import { useAuth } from "@/contexts/AuthContext";
import { getSharedTokenDebugMeta, resolveSharedDeckToken } from "@/lib/sharedLinks";

const DEFAULT_STUDY_MODES: StudyModes = { cards: true, learn: true, test: true, match: true, philology: true };

type SharedDeckMode = "cards" | "learn" | "test" | "games";

function logSharedDeck(event: string, payload?: Record<string, unknown>) {
  if (typeof console.debug === "function") {
    console.debug("[StudyDo shared_deck]", event, payload ?? {});
  }
}

function logSharedLink(event: string, payload?: Record<string, unknown>) {
  if (typeof console.debug === "function") {
    console.debug("[StudyDo shared_link]", event, payload ?? {});
  }
}

export default function SharedDeckPage() {
  const { t, i18n } = useTranslation();
  const { token: routeToken } = useParams<{ token: string }>();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlModeParam = searchParams.get("mode");
  const urlMode: SharedDeckMode =
    urlModeParam === "learn" || urlModeParam === "test" || urlModeParam === "games" ? urlModeParam : "cards";
  const navigate = useNavigate();
  const { user } = useAuth();
  const publicHomeHref = user ? "/" : "/landing";
  const [addToAccountLoading, setAddToAccountLoading] = useState(false);
  const [alreadyInLibraryMsg, setAlreadyInLibraryMsg] = useState<string | null>(null);
  const routeVariant = location.pathname.startsWith("/shared/") ? "shared" : "share";
  const resolvedTokenInfo = useMemo(
    () => resolveSharedDeckToken({ routeToken, pathname: location.pathname, searchParams }),
    [routeToken, location.pathname, searchParams]
  );
  const token = resolvedTokenInfo.token;
  const tokenDebugMeta = useMemo(
    () => getSharedTokenDebugMeta(token, resolvedTokenInfo.source),
    [token, resolvedTokenInfo.source]
  );

  useEffect(() => {
    logSharedLink("shared_link_open_started", {
      routeVariant,
      pathname: location.pathname,
    });
  }, [routeVariant, location.pathname]);

  useEffect(() => {
    if (token) {
      logSharedLink("shared_link_token_resolved", {
        routeVariant,
        ...tokenDebugMeta,
      });
      return;
    }

    logSharedLink("shared_link_invalid_token", {
      routeVariant,
      ...tokenDebugMeta,
    });
  }, [routeVariant, token, tokenDebugMeta]);

  const handleFetchError = useCallback((err: unknown) => {
    logSharedLink("shared_link_fetch_failed", {
      routeVariant,
      ...tokenDebugMeta,
      reason: "request_error",
    });
    inspectQueryError({
      queryName: "fetchSharedDeck",
      flow: "shared.loadDeck",
      table: "decks",
      filters: { share_token_present: tokenDebugMeta.tokenPresent, token_source: tokenDebugMeta.tokenSource },
      responseError: err,
    });
  }, [routeVariant, tokenDebugMeta]);

  const { state, isLoading, hasError } = useSafeAsyncData<Deck | null>({
    fetch: async () => {
      if (!token) return null;

      logSharedLink("shared_link_fetch_started", {
        routeVariant,
        ...tokenDebugMeta,
      });

      const result = await fetchSharedDeck(token);
      if (result) {
        logSharedLink("shared_link_fetch_succeeded", {
          routeVariant,
          ...tokenDebugMeta,
          deckId: result.id,
          cardsCount: result.cards?.length ?? 0,
        });
      } else {
        logSharedLink("shared_link_fetch_failed", {
          routeVariant,
          ...tokenDebugMeta,
          reason: "empty_result",
        });
      }

      return result;
    },
    deps: [token, resolvedTokenInfo.source, routeVariant],
    emptyIsSuccess: true,
    onError: handleFetchError,
  });

  const deck = state.status === "success" && state.data ? state.data : null;
  const loading = isLoading;
  const notFound = !token || (state.status === "success" && state.data == null) || hasError;
  const searchSuffix = searchParams.toString();

  useEffect(() => {
    if (!notFound) return;
    logSharedLink("shared_link_not_found_rendered", {
      routeVariant,
      ...tokenDebugMeta,
      reason: !token ? "invalid_token" : hasError ? "fetch_error" : "missing_deck",
    });
  }, [routeVariant, token, tokenDebugMeta, notFound, hasError]);

  useEffect(() => {
    const canonicalToken =
      typeof deck?.shareToken === "string" && deck.shareToken.trim() !== ""
        ? deck.shareToken.trim()
        : null;
    if (!canonicalToken || !token || canonicalToken === token) return;

    const nextUrl = `/${routeVariant}/${canonicalToken}${searchSuffix ? `?${searchSuffix}` : ""}`;
    navigate(nextUrl, { replace: true });
  }, [deck?.shareToken, navigate, routeVariant, searchSuffix, token]);

  const studyModes: StudyModes = useMemo(
    () => (deck?.studyModes ? { ...DEFAULT_STUDY_MODES, ...deck.studyModes } : DEFAULT_STUDY_MODES),
    [deck?.studyModes]
  );
  const modesConfig: { id: SharedDeckMode; labelKey: string; enabled: boolean }[] = useMemo(
    () => [
      { id: "cards", labelKey: "deckModal.studyModeCards", enabled: !!studyModes.cards },
      { id: "learn", labelKey: "deckModal.studyModeLearn", enabled: !!studyModes.learn },
      { id: "test", labelKey: "deckModal.studyModeTest", enabled: !!studyModes.test },
      { id: "games", labelKey: "studyMenu.games", enabled: !!studyModes.match },
      { id: "philology", labelKey: "deckModal.studyModePhilology", enabled: !!studyModes.philology },
    ],
    [studyModes]
  );
  const availableModes = useMemo(() => modesConfig.filter((m) => m.enabled), [modesConfig]);
  const effectiveMode: SharedDeckMode =
    modesConfig.find((m) => m.id === urlMode && m.enabled) ? urlMode : availableModes[0]?.id ?? "cards";

  useEffect(() => {
    if (!deck || !token) return;
    logSharedDeck("shared_deck_page_loaded", { deckId: deck.id, mode: effectiveMode, routeVariant, ...tokenDebugMeta });
    logSharedDeck("shared_deck_actions_visible", {
      deckId: deck.id,
      addButtonVisible: true,
      modeTabsCount: availableModes.length,
      routeVariant,
      ...tokenDebugMeta,
    });
  }, [deck?.id, token, effectiveMode, availableModes.length, routeVariant, tokenDebugMeta]);

  useEffect(() => {
    if (!deck || availableModes.length === 0) return;
    logSharedDeck("shared_deck_mode_tabs_rendered", {
      deckId: deck.id,
      modes: availableModes.map((m) => m.id),
      routeVariant,
      ...tokenDebugMeta,
    });
  }, [deck?.id, availableModes.length, routeVariant, tokenDebugMeta]);

  const setMode = useCallback(
    (mode: SharedDeckMode) => {
      if (mode === "cards") {
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.delete("mode");
          return next;
        }, { replace: true });
      } else {
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.set("mode", mode);
          return next;
        }, { replace: true });
      }
      logSharedDeck("shared_deck_study_launch_started", { deckId: deck?.id, mode, routeVariant, ...tokenDebugMeta });
    },
    [deck?.id, routeVariant, setSearchParams, tokenDebugMeta]
  );

  // Flashcard state
  const [cardIndex, setCardIndex] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [score, setScore] = useState({ correct: 0, incorrect: 0 });
  const [finished, setFinished] = useState(false);
  const [showExplain, setShowExplain] = useState(false);

  const handleAddToAccount = async () => {
    if (!user) {
      navigate("/auth", { state: { returnTo: token ? `/${routeVariant}/${token}` : "/share" } });
      return;
    }
    if (!deck) return;
    logSharedDeck("shared_deck_add_to_account_started", { deckId: deck.id, routeVariant, ...tokenDebugMeta });
    setAddToAccountLoading(true);
    setAlreadyInLibraryMsg(null);
    try {
      const cloned = await createDeck(
        user.id,
        {
          title: deck.title,
          lang: deck.lang,
          emoji: deck.emoji,
          level: deck.level,
          deckType: deck.deckType,
          cards: deck.cards,
        },
        { sourceDeckId: deck.id }
      );
      if (cloned.cards?.length) {
        triggerBackgroundTtsForCards(cloned.cards, cloned.lang ?? "en");
      }
      if (shouldValidateReadAfterWrite("shared.addDeckToAccount")) {
        validateWriteReadConsistency({
          flow: "shared.addDeckToAccount",
          entity: "deck",
          entityId: cloned.id,
          userId: user.id,
          writeSucceeded: true,
          expectedPresence: true,
          fetchValidator: () => fetchDecksLight(user.id),
          checkPresence: (list) => Array.isArray(list) && list.some((d) => d.id === cloned.id),
        }).catch(() => {});
      }
      logSharedDeck("shared_deck_add_to_account_succeeded", {
        deckId: deck.id,
        newDeckId: cloned.id,
        routeVariant,
        ...tokenDebugMeta,
      });
      navigate("/", { replace: true });
    } catch (err) {
      if (err instanceof DeckAlreadyImportedError) {
        setAlreadyInLibraryMsg(t("shared.deckAlreadyInLibrary"));
        setAddToAccountLoading(false);
        return;
      }
      inspectQueryError({
        queryName: "createDeck",
        flow: "shared.addDeckToAccount",
        table: "decks",
        responseError: err,
      });
      console.error("Failed to add deck:", err);
      setAddToAccountLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={centerStyle}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🎴</div>
        <div style={{ color: "hsl(var(--muted-foreground))", fontFamily: "DM Sans, sans-serif" }}>
          {t("shared.loadingDeck")}
        </div>
      </div>
    );
  }

  if (notFound || !deck) {
    return (
      <div style={centerStyle}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>🔒</div>
        <div style={{ fontSize: 20, fontWeight: 600, marginBottom: 8, fontFamily: "DM Sans, sans-serif" }}>
          {t("shared.deckNotFound")}
        </div>
        <div style={{ color: "hsl(var(--muted-foreground))", fontFamily: "DM Sans, sans-serif", marginBottom: 24 }}>
          {t("shared.deckNotFoundHint")}
        </div>
        <Link
          to={publicHomeHref}
          style={{
            padding: "10px 24px",
            borderRadius: 50,
            background: "hsl(var(--primary))",
            color: "white",
            textDecoration: "none",
            fontFamily: "DM Sans, sans-serif",
            fontSize: 14,
            fontWeight: 500,
          }}
        >
          {t("shared.goToLinguaDeck")}
        </Link>
      </div>
    );
  }

  const cards = deck.cards ?? [];
  const currentCard = cards[cardIndex];
  const uiLang = (i18n.language || "en").split("-")[0];

  const handleNext = (correct: boolean) => {
    setScore((s) => ({
      correct: s.correct + (correct ? 1 : 0),
      incorrect: s.incorrect + (correct ? 0 : 1),
    }));
    if (cardIndex + 1 >= cards.length) {
      setFinished(true);
    } else {
      setCardIndex((i) => i + 1);
      setFlipped(false);
    }
  };

  const handleRestart = () => {
    setCardIndex(0);
    setFlipped(false);
    setScore({ correct: 0, incorrect: 0 });
    setFinished(false);
  };

  const goPrev = () => {
    if (cardIndex > 0) {
      setCardIndex((i) => i - 1);
      setFlipped(false);
    }
  };

  const goNext = () => {
    if (cardIndex + 1 < cards.length) {
      setCardIndex((i) => i + 1);
      setFlipped(false);
    }
  };

  const scorePillClass =
    "inline-flex min-w-[7rem] items-center justify-center rounded-2xl border px-4 py-3 text-sm font-semibold";

  return (
    <div
      className="flex min-h-[100svh] flex-col overflow-x-hidden"
      style={{
        minHeight: "100dvh",
        background: "hsl(var(--bg))",
        fontFamily: "DM Sans, sans-serif",
      }}
    >
      <div className="border-b border-[hsl(var(--border))] bg-[hsl(var(--bg2))]/95 pt-[env(safe-area-inset-top)]">
        <div className="mx-auto flex w-full max-w-6xl flex-col gap-3 px-4 py-3 sm:px-6">
          <div className="flex items-center justify-between gap-3">
            <Link to={publicHomeHref} className="flex min-w-0 items-center gap-3 no-underline">
              <div
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl sm:h-9 sm:w-9"
                style={{ background: "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--accent2)))" }}
              >
                <BookOpen className="h-4 w-4 text-white sm:h-5 sm:w-5" />
              </div>
              <div className="min-w-0">
                <span className="block truncate text-lg font-bold sm:text-xl" style={{ color: "hsl(var(--foreground))" }}>
                  StudyDo
                </span>
                <div
                  className="truncate text-[10px] italic sm:text-[11px]"
                  style={{ color: "hsl(var(--muted-foreground))" }}
                >
                  {t("shared.sharedDeckReadOnly")}
                </div>
              </div>
            </Link>

            <div className="hidden items-center gap-3 md:flex">
              {alreadyInLibraryMsg && (
                <span className="text-sm text-[hsl(var(--muted-foreground))] max-w-[12rem] truncate" title={alreadyInLibraryMsg}>
                  {alreadyInLibraryMsg}
                </span>
              )}
              <LanguageSwitcher compact />
              <ThemeToggle compact />
              <button
                onClick={handleAddToAccount}
                disabled={addToAccountLoading}
                className="inline-flex shrink-0 items-center justify-center rounded-full px-4 py-2.5 text-sm font-semibold text-white transition-opacity disabled:cursor-wait disabled:opacity-80"
                style={{
                  border: "none",
                  background: "hsl(var(--primary))",
                  fontFamily: "DM Sans, sans-serif",
                }}
              >
                {addToAccountLoading ? "…" : t("shared.addDeckToAccount")}
              </button>
            </div>
          </div>

            <div className="flex flex-col gap-2 md:hidden">
            <div className="flex flex-wrap items-center gap-2">
              <LanguageSwitcher compact />
              <ThemeToggle compact />
            </div>
            {alreadyInLibraryMsg && (
              <div className="text-sm text-[hsl(var(--muted-foreground))]">{alreadyInLibraryMsg}</div>
            )}
            <button
              onClick={handleAddToAccount}
              disabled={addToAccountLoading}
              className="inline-flex w-full items-center justify-center rounded-full px-4 py-2.5 text-sm font-semibold text-white transition-opacity disabled:cursor-wait disabled:opacity-80 sm:w-auto sm:self-start"
              style={{
                border: "none",
                background: "hsl(var(--primary))",
                fontFamily: "DM Sans, sans-serif",
              }}
            >
              {addToAccountLoading ? "…" : t("shared.addDeckToAccount")}
            </button>
          </div>
        </div>
      </div>

      <div className="mx-auto flex w-full max-w-6xl flex-1 flex-col px-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3 sm:px-4 md:px-6 md:pt-5">
        {availableModes.length > 0 && (
          <div className="mb-4 flex flex-wrap items-center gap-2">
            {modesConfig.filter((m) => m.enabled).map(({ id, labelKey }) => (
              <button
                key={id}
                type="button"
                onClick={() => setMode(id)}
                className={`inline-flex h-9 min-w-[4rem] items-center justify-center rounded-xl border px-3 text-sm font-medium transition-all duration-150 sm:h-10 sm:px-4 ${
                  effectiveMode === id
                    ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))] text-white shadow-sm active:scale-[0.98]"
                    : "border-[hsl(var(--border))] bg-[hsl(var(--bg3))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--border))]/10 active:scale-[0.98]"
                }`}
                aria-pressed={effectiveMode === id}
              >
                {id === "games" ? t("home.tabsGames") : t(labelKey)}
              </button>
            ))}
          </div>
        )}

        {finished ? (
          <div
            className="mx-auto w-full max-w-[26rem] rounded-[var(--radius)] border px-6 py-8 text-center sm:px-8 sm:py-10"
            style={{ background: "hsl(var(--card-bg))", borderColor: "hsl(var(--border))" }}
          >
            <div style={{ fontSize: 48, marginBottom: 16 }}>🎉</div>
            <div style={{ fontSize: 22, fontWeight: 700, marginBottom: 8 }}>{t("shared.sessionComplete")}</div>
            <div style={{ fontSize: 14, color: "hsl(var(--muted-foreground))", marginBottom: 24 }}>
              {t("shared.cardsReviewed", { count: cards.length })}
            </div>
            <div style={{ display: "flex", gap: 16, justifyContent: "center", marginBottom: 28 }}>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 32, fontWeight: 700, color: "hsl(var(--accent2))" }}>{score.correct}</div>
                <div style={{ fontSize: 12, color: "hsl(var(--muted-foreground))" }}>{t("shared.correct")}</div>
              </div>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 32, fontWeight: 700, color: "hsl(var(--accent3))" }}>{score.incorrect}</div>
                <div style={{ fontSize: 12, color: "hsl(var(--muted-foreground))" }}>{t("shared.incorrect")}</div>
              </div>
            </div>
            <button
              onClick={handleRestart}
              style={{
                padding: "10px 28px",
                borderRadius: 50,
                border: "none",
                background: "hsl(var(--primary))",
                color: "white",
                fontSize: 14,
                fontWeight: 500,
                cursor: "pointer",
                fontFamily: "DM Sans, sans-serif",
              }}
            >
              🔄 {t("shared.playAgain")}
            </button>
          </div>
        ) : effectiveMode === "learn" ? (
          <div className="mx-auto w-full max-w-2xl px-3">
            <LearnMode
              cards={cards.filter((c) => (c.front || "").trim() && (c.back || "").trim())}
              deckLang={deck.lang ?? "en"}
              onBack={() => setMode("cards")}
              onSessionEnd={() => {}}
            />
          </div>
        ) : effectiveMode === "test" ? (
          <div className="mx-auto w-full max-w-2xl flex-1 flex flex-col min-h-0 px-3">
            <TestStudyView
              deck={deck}
              onBack={() => setMode("cards")}
              onNext={() => {}}
              onMark={() => {}}
            />
          </div>
        ) : effectiveMode === "games" ? (
          <div className="mx-auto w-full max-w-4xl">
            <div className="mb-4 flex items-center gap-2">
              <span className="text-sm font-semibold text-[hsl(var(--foreground))]">{t("deckDashboard.chooseGame")}</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {[
                { id: "match", labelKey: "deckModal.studyModeMatch", icon: Shuffle },
                { id: "cat-mouse", labelKey: "games.catMouse", icon: Zap },
                { id: "defender", labelKey: "defender.title", icon: Rocket },
                { id: "tower", labelKey: "tower.title", icon: Building2 },
                { id: "memory-jumble", labelKey: "games.memoryJumble", icon: ListOrdered },
                { id: "memory", labelKey: "games.memory", icon: ScanSearch },
                { id: "rebus", labelKey: "games.rebus", icon: Puzzle },
                { id: "iq", labelKey: "games.oddOneOut", icon: Brain },
                { id: "audio", labelKey: "games.audioClouds", icon: Headphones },
                { id: "blitz", labelKey: "games.blitz", icon: Zap },
                { id: "swipe", labelKey: "games.swipe", icon: Layers },
                { id: "mosaic", labelKey: "games.mosaic", icon: Grid },
              ].map(({ id, labelKey, icon: Icon }) => (
                <Link
                  key={id}
                  to={token ? `/${routeVariant}/${token}/study/${id}` : "/"}
                  className="flex flex-col items-center justify-center gap-2.5 p-4 rounded-xl border-2 border-[hsl(var(--border))] bg-[hsl(var(--card-bg))] transition-all duration-200 hover:border-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/10 min-h-[100px] no-underline text-[hsl(var(--foreground))]"
                >
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 bg-[hsl(var(--muted))]">
                    <Icon size={22} className="text-[hsl(var(--primary))]" strokeWidth={2} />
                  </div>
                  <span className="text-sm font-medium text-center line-clamp-2 leading-tight">
                    {t(labelKey)}
                  </span>
                </Link>
              ))}
            </div>
            <p className="mt-4 text-xs text-[hsl(var(--muted-foreground))]">
              {t("shared.gamesNoProgressHint", { defaultValue: "Progress is not saved on shared links. Add to your library to save progress." })}
            </p>
          </div>
        ) : (
          <div className="grid min-h-0 flex-1 gap-4 xl:grid-cols-[minmax(0,1fr)_19rem] xl:items-start xl:gap-5">
            <div className="min-w-0 min-h-0 rounded-[1.75rem] border border-[hsl(var(--border))] bg-[hsl(var(--card-bg))] px-2 py-2 shadow-sm sm:px-3 sm:py-3">
              <FlashcardView
                deck={deck}
                cardIndex={cardIndex}
                cardFlipped={flipped}
                isTeacher={false}
                onFlip={() => setFlipped((f) => !f)}
                onMark={handleNext}
                onShuffle={() => {}}
                onShowExplain={() => setShowExplain(true)}
                onPrevCard={goPrev}
                onNextCard={goNext}
                readOnly
              />
              <div className="flex flex-wrap items-center justify-center gap-2 px-2 pb-2 pt-1 text-center sm:gap-3">
                <span
                  className={`${scorePillClass} border-emerald-400/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-300`}
                >
                  ✓ {t("shared.correct")}: {score.correct}
                </span>
                <span
                  className={`${scorePillClass} border-rose-400/30 bg-rose-500/10 text-rose-600 dark:text-rose-300`}
                >
                  ✗ {t("shared.incorrect")}: {score.incorrect}
                </span>
              </div>
            </div>

            <div className="min-w-0 flex flex-col gap-3 xl:sticky xl:top-5">
              <div
                className="hidden rounded-[1.5rem] border px-4 py-4 shadow-sm xl:block"
                style={{ background: "hsl(var(--card-bg))", borderColor: "hsl(var(--border))" }}
              >
                <div className="text-xs font-semibold uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))]">
                  {t("shared.sharedDeckReadOnly")}
                </div>
                <div className="mt-2 text-lg font-semibold text-[hsl(var(--foreground))]">{deck.title}</div>
                <div className="mt-1 text-sm text-[hsl(var(--muted-foreground))]">
                  {t("flashcard.cardCounter", { current: cardIndex + 1, total: cards.length })}
                </div>
                {alreadyInLibraryMsg && (
                  <div className="mt-4 text-sm text-[hsl(var(--muted-foreground))]">{alreadyInLibraryMsg}</div>
                )}
                <button
                  onClick={handleAddToAccount}
                  disabled={addToAccountLoading}
                  className="mt-4 inline-flex w-full items-center justify-center rounded-full px-4 py-2.5 text-sm font-semibold text-white transition-opacity disabled:cursor-wait disabled:opacity-80"
                  style={{
                    border: "none",
                    background: "hsl(var(--primary))",
                    fontFamily: "DM Sans, sans-serif",
                  }}
                >
                  {addToAccountLoading ? "…" : t("shared.addDeckToAccount")}
                </button>
              </div>

              <div
                className="rounded-[1.5rem] border px-4 py-4 text-center shadow-sm"
                style={{ background: "hsl(var(--card-bg))", borderColor: "hsl(var(--border))" }}
              >
                <div className="text-sm font-semibold text-[hsl(var(--foreground))]">{t("shared.wantToCreateDecks")}</div>
                <div className="mt-1 text-sm text-[hsl(var(--muted-foreground))]">
                  {t("shared.promoSignUp")}
                </div>
                <Link
                  to="/auth"
                  className="mt-4 inline-flex items-center justify-center rounded-full px-5 py-2.5 text-sm font-semibold text-white no-underline"
                  style={{ background: "hsl(var(--primary))" }}
                >
                  {t("shared.getStartedFree")}
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Explain modal */}
      {showExplain && currentCard && deck && (
        <ExplainModal
          card={currentCard}
          deck={deck}
          uiLang={uiLang}
          onClose={() => setShowExplain(false)}
        />
      )}
    </div>
  );
}

const centerStyle: React.CSSProperties = {
  minHeight: "100dvh",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  background: "hsl(var(--bg))",
  fontFamily: "DM Sans, sans-serif",
  textAlign: "center",
  padding: 24,
};
